// sso-redirect.page.ts
import { Component, Injector, OnInit } from '@angular/core';
import { UserCredential } from '@angular/fire/auth';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import { filter } from 'rxjs';
import * as SsoActions from '../../../../../../aumico-lib-users-fe/src/lib/actions/sso.actions';
import { SsoOidcDto, SSOProtocol } from '../../../../../../aumico-lib-users-fe/src/lib/dtos/ssoOidcDto.interface';
import { NgRxDefaultSsoProvider } from '../../../../../../aumico-lib-users-fe/src/lib/provider/sso.provider';
import { SsoState } from '../../../../../../aumico-lib-users-fe/src/lib/reducers/sso.reducer';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { Paths } from '../../../enums/paths.enum';
import { UsersNavigationService } from '../../../shared/services/users.navigation.service';
import { BasePageComponent } from '../../base.page';

// If you installed Firebase via <script> in index.html, you can refer to the global 'firebase'.
// Otherwise, if you installed via npm, do: import * as firebase from 'firebase/app'; import 'firebase/auth';
declare const firebase: any; // assume v8 “firebase” is loaded globally

const debug = Debug('aumico:aumico-project-reports:SsoRedirectPageComponent');

@UntilDestroy()
@Component({
    selector: 'app-auth-sso-redirect',
    templateUrl: './sso-redirect.page.html',
    styleUrls: ['./sso-redirect.page.scss'],
})
export class SsoRedirectPageComponent extends BasePageComponent implements OnInit {
    // Holds the fetched provider (once NgRx loads it)
    ssoProvider: SsoOidcDto | null = null;
    loading = true;
    hasError = false;
    error: string | null = null;

    constructor(
        injector: Injector,
        private usersNavigationService: UsersNavigationService,
        private readonly mixpanel: MixpanelService,
        private readonly ngrxProvider: NgRxDefaultSsoProvider,
        private route: ActivatedRoute,
        private store: Store<SsoState>,
    ) {
        super(injector);
    }

    get usePopupLoginOIDC(): boolean {
        if (this.ssoProvider.protocol === SSOProtocol.OPENID_CONNECT) {
            return true;
        } else {
            return false; // For other protocols, we use redirect
        }
    }
    get useRedirectLoginOIDC(): boolean {
        if (this.ssoProvider.protocol === SSOProtocol.OPENID_CONNECT_CUSTOM) {
            return true;
        } else {
            return false; // For other protocols, we use redirect
        }
    }
    get usePopupLoginSAML(): boolean {
        if (this.ssoProvider.protocol === SSOProtocol.SAML) {
            return true;
        } else {
            return false; // For other protocols, we use redirect
        }
    }

    override ngOnInit() {
        super.ngOnInit();

        // 1) read “handle” from URL and dispatch the NgRx action
        this.route.paramMap.pipe(untilDestroyed(this)).subscribe((params) => {
            const handle = params.get('handle');
            if (!handle) {
                this.router.navigateByUrl('/');
                return;
            }
            debug('dispatch SSO handle:', handle);
            this.store.dispatch(SsoActions.loadSsoProvider({ handle }));
        });

        // 2) subscribe to the NgRx‐backed provider stream
        this.ngrxProvider
            .getSsoOIDC$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (prov) => {
                    this.loading = false;
                    if (prov && prov.integrationHandle) {
                        this.ssoProvider = prov;
                        this.hasError = false;
                    } else {
                        this.hasError = true;
                        this.error = 'No SSO provider found for that handle.';
                    }
                },
                error: (err) => {
                    this.loading = false;
                    this.hasError = true;
                    this.error = err?.message || 'Error loading SSO provider';
                },
            });

        this.usersNavigationService.readyToRoute$
            .pipe(
                filter((readyToRoute) => {
                    return readyToRoute !== null && readyToRoute !== undefined;
                }),
                untilDestroyed(this),
            )
            .subscribe((lang) => this.routeToFsOverViewPage(lang));
    }

    onLoginWithPopupOIDC() {
        if (!this.ssoProvider) {
            return;
        }
        // Pass the provider ID you registered in Firebase Auth, e.g. "oidc.auth0"
        this.authService.loginWithOidc(this.ssoProvider.integrationName).subscribe({
            next: (userCred: UserCredential) => {
                // If you want to double-check claims right here:
                userCred.user.getIdTokenResult(true).then((tokenResult) => {
                    // You can assert that tokenResult.claims.roles and organizationId are set
                });

                this.authProvider.dispatchGetCurrentUser();
            },
            error: (err) => {
                console.error('SSO login failed:', err);
                this.error = err.message || 'SSO login failed';
            },
        });
    }

    onLoginWithPopupSAML() {
        if (!this.ssoProvider) {
            return;
        }
        // Pass the provider ID you registered in Firebase Auth, e.g. "oidc.auth0"
        this.authService.loginWithSaml(this.ssoProvider.integrationName).subscribe({
            next: (userCred: UserCredential) => {
                // If you want to double-check claims right here:
                userCred.user.getIdTokenResult(true).then((tokenResult) => {
                    // You can assert that tokenResult.claims.roles and organizationId are set
                });

                this.authProvider.dispatchGetCurrentUser();
            },
            error: (err) => {
                console.error('SSO login failed:', err);
                this.error = err.message || 'SSO login failed';
            },
        });
    }

    // ——————————————————————————————————————————————————
    // 2) Redirect‐based flow (custom‐token). No popup,
    //    we navigate the browser to /auth/sso/:handle/start
    // ——————————————————————————————————————————————————
    onLoginRedirectClick() {
        if (!this.ssoProvider) {
            return;
        }
        const startUrl = this.authService.getStartSsoUrl(this.ssoProvider.integrationHandle);
        window.location.href = startUrl; // browser navigates away
    }

    onCloseClick() {
        this.router.navigateByUrl('/');
    }

    private routeToFsOverViewPage(lang: string): void {
        this.router.navigate([`/${lang}/${Paths.HOME}`]);
    }
}
